/**
 * Add Field Dialog
 *
 * Dialog for adding a new field to a table schema.
 * Includes validation for field name, type selection, and constraints.
 */

import { useState, useEffect } from 'react';
import { Plus, X, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@loader/ui';
import { Button } from '@loader/ui';
import { Input } from '@loader/ui';
import { Label } from '@loader/ui';
import { Checkbox } from '@loader/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import { Alert, AlertDescription } from '@loader/ui';
import { FIELD_DATA_TYPES, FIELD_DATA_TYPE_LABELS, type FieldDataType } from '@loader/shared-types';

import { toast } from '@/lib/toast';
import { useCreateFieldMutation } from '@/features/tables/hooks/useTableQueries';
import { getTypeIcon } from '@/features/schemas/constants/fieldTypes';

interface AddFieldDialogProps {
  tableId: string;
  existingFieldNames: string[];
  nextOrderIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddFieldDialog: React.FC<AddFieldDialogProps> = ({
  tableId,
  existingFieldNames,
  nextOrderIndex,
  isOpen,
  onClose,
  onSuccess,
}) => {
  // Form state
  const [name, setName] = useState('');
  const [dataType, setDataType] = useState<FieldDataType>('text');
  const [isRequired, setIsRequired] = useState(false);
  const [isUnique, setIsUnique] = useState(false);
  const [description, setDescription] = useState('');

  // Validation state
  const [nameError, setNameError] = useState('');

  // Mutation
  const createFieldMutation = useCreateFieldMutation(tableId);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDataType('text');
      setIsRequired(false);
      setIsUnique(false);
      setDescription('');
      setNameError('');
    }
  }, [isOpen]);

  // When unique is enabled, required is automatically enabled
  useEffect(() => {
    if (isUnique && !isRequired) {
      setIsRequired(true);
    }
  }, [isUnique, isRequired]);

  const validateName = (value: string): boolean => {
    if (!value.trim()) {
      setNameError('Field name is required');
      return false;
    }

    // Check for duplicates (case-insensitive)
    const normalizedName = value.trim().toLowerCase();
    const isDuplicate = existingFieldNames.some(
      (existing) => existing.toLowerCase() === normalizedName
    );

    if (isDuplicate) {
      setNameError('A field with this name already exists');
      return false;
    }

    setNameError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateName(name)) {
      return;
    }

    try {
      const result = await createFieldMutation.mutateAsync({
        name: name.trim(),
        data_type: dataType,
        is_required: isRequired,
        is_unique: isUnique,
        description: description.trim() || undefined,
        order_index: nextOrderIndex,
      });

      // Show warnings if any (success case has immediate visual feedback in schema view)
      if (result.warnings && result.warnings.length > 0) {
        toast.warning('Field created with warnings', result.warnings.join(', '));
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error('Failed to add field', error instanceof Error ? error.message : undefined);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Field</DialogTitle>
          <DialogDescription>
            Add a new field to the table schema.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Field Name */}
          <div className="space-y-2">
            <Label htmlFor="field-name">
              Field Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="field-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) validateName(e.target.value);
              }}
              onBlur={() => validateName(name)}
              placeholder="e.g., customer_id"
              autoFocus
              aria-required="true"
              aria-invalid={!!nameError}
              aria-describedby={nameError ? 'field-name-error' : undefined}
              className={nameError ? 'border-destructive' : ''}
            />
            {nameError && (
              <p id="field-name-error" className="text-destructive text-sm">
                {nameError}
              </p>
            )}
          </div>

          {/* Data Type */}
          <div className="space-y-2">
            <Label htmlFor="field-type">Data Type</Label>
            <Select value={dataType} onValueChange={(val) => setDataType(val as FieldDataType)}>
              <SelectTrigger id="field-type" className="w-full">
                <SelectValue placeholder="Select a type" />
              </SelectTrigger>
              <SelectContent>
                {FIELD_DATA_TYPES.map((type) => {
                  const Icon = getTypeIcon(type);
                  return (
                    <SelectItem key={type} value={type}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span>{FIELD_DATA_TYPE_LABELS[type]}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Constraints */}
          <div className="space-y-3">
            <Label>Constraints</Label>
            <div className="flex flex-col gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="field-required"
                  checked={isRequired}
                  onCheckedChange={(checked) => {
                    // Cannot disable required if unique is enabled
                    if (!checked && isUnique) return;
                    setIsRequired(checked as boolean);
                  }}
                  disabled={isUnique}
                />
                <Label htmlFor="field-required" className="cursor-pointer font-normal">
                  Required
                  <span className="text-muted-foreground ml-1 text-xs">
                    (field cannot be empty)
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="field-unique"
                  checked={isUnique}
                  onCheckedChange={(checked) => setIsUnique(checked as boolean)}
                />
                <Label htmlFor="field-unique" className="cursor-pointer font-normal">
                  Unique
                  <span className="text-muted-foreground ml-1 text-xs">
                    (no duplicate values allowed)
                  </span>
                </Label>
              </div>
            </div>
            {isUnique && (
              <Alert variant="default" className="bg-muted/50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Unique fields are automatically required.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="field-description">Description</Label>
            <Input
              id="field-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description for this field"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createFieldMutation.isPending}>
            {createFieldMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Add Field
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
